/**
 * LSP wire vocabulary — the string constants the backend and every FE / Node consumer must agree
 * on when talking to the Prophecy WebSocket endpoints.
 *
 * SHARED by design: the frontend agent chat, the prophecy-mcp connector, and any future Node/CLI
 * tooling all speak the same protocol. Anywhere else, use the enum values — never string literals —
 * so a rename here catches every drift at typecheck.
 *
 * The FE-only helpers (`MethodType`, `ExtractParams`, …) that depended on `Callable` stay in
 * frontend/core/src/LSP/base/types.ts; they exist to type the useLSPMethod React hook and are
 * meaningless outside the browser.
 */

export namespace LSP {
  export enum Notification {
    ready = 'ready',
    open = 'open',
    updateExecutableCode = 'updateExecutableCode',
    close = 'close',
    error = 'error',
    propertiesPublishDiagnostics = 'properties/publishDiagnostics',
    logMessage = 'window/logMessage',
    componentsDidOpen = 'components/didOpen',
    componentsDidUpdate = 'components/didUpdate',
    propertiesDidOpen = 'properties/didOpen',
    // loads the filesystem with file content into `$.fileSystem`
    fileSystemDidOpen = 'fileSystem/didOpen',
    featuresDidOpen = 'features/didOpen',
    metadataPropertiesDidOpen = 'metadataProperties/didOpen',
    // The provider's column-type catalog: what Prophecy calls each Arrow type, and which types a
    // user may pick. Sent once per session, alongside the other didOpen bursts.
    typesDidOpen = 'types/didOpen',
    propertiesDidUpdate = 'properties/didUpdate',
    propertiesDidMasterUpdate = 'properties/didMasterUpdate',
    propertiesDidSchemaUpdate = 'properties/didSchemaUpdate',
    metadataDidOpen = 'metadata/didOpen',
    metadataDidUpdate = 'metadata/didUpdate',
    metadataReloadNeeded = 'metadata/reloadNeeded',
    compileProgress = 'compile/progress',
    textDocumentsDidUpdate = 'textDocuments/didUpdate',
    textDocumentsDidOpen = 'textDocuments/didOpen',
    // textDocumentPublishDiagnostics = 'textDocument/publishDiagnostics',
    textDocumentsPublishDiagnostics = 'textDocuments/publishDiagnostics',
    textDocumentsMetadataDidChange = 'textDocumentsMetadata/didUpdate',
    dependencyUpdate = 'dependency/update',
    ping = 'ping',
    // Dependencies
    dependenciesDidResolve = 'dependencies/resolved',
    windowShowMessage = 'window/showMessage',
    // Copilot
    copilotTranscribe = 'copilot/transcribe',

    copilotAgentProgress = 'copilot/agentProgress',
    copilotKinesisEvents = 'copilot/kinesisEvents',

    copilotDocumentProgress = 'copilot/documentProgress',

    // Copilot Project Agent Chat Message API
    copilotAgentChatMessageDidUpdate = 'copilot/chat/didUpdate',
    copilotAgentEvent = 'copilot/chat/event',
    copilotSnapshotConversationLoaded = 'copilot/snapshotConversationLoaded',
    // Locks
    lockUpdate = 'lock/didUpdate',
    lockTakeOverResponse = 'lock/takeoverResponse',
    lockRequestRelease = 'lock/requestRelease',
    // Collaborators
    collaborators = 'users/update',
    // Broadcast Notifications
    broadcastMessageReceived = 'message/didReceive',
    // session
    sessionDidOpen = 'session/didOpen',

    // backend message to hint frontend for anything
    hint = 'message/hint',
    propertiesDidFeAction = 'properties/didFeAction',

    // notification to relay the filesystem sync from IDE to orchestrator through FE
    // when IDE does not have direct access to the orchestrator
    rdcRelay = 'rdc/relay',

    messageQueueSync = 'messageQueue/sync',

    /**
     * Chunked-streaming protocol used by the backend to stream large payloads.
     * `chunkStart`/`chunkEnd` are control frames; the slices in between are raw
     * strings that, once concatenated and JSON-parsed, form a single message.
     * `chunkProgress` is synthesized on the frontend for each received slice so
     * message listeners (e.g. keep-alive) stay informed while a stream is in flight.
     * @see WebSocketBase._onMessage
     */
    chunkStart = 'chunk/start',
    chunkEnd = 'chunk/end',
    chunkProgress = 'chunk/progress'
  }

  export enum Method {
    initialize = 'initialize',

    textDocumentDidCreate = 'textDocument/didCreate',
    textDocumentDidDelete = 'textDocument/didDelete',
    textDocumentDidMove = 'textDocument/didMove',
    textDocumentDidSwitch = 'textDocument/didSwitch',
    createGem = 'editor/createGem',

    textDocumentDidChange = 'textDocument/didChange',
    textDocumentDidSave = 'textDocument/didSave',
    textDocumentDidCompile = 'textDocument/didCompile',
    textDocumentDidPublish = 'textDocument/didPublish',
    propertiesDidChange = 'properties/didChange',
    propertiesDidPromote = 'properties/didPromote',
    propertiesDidSave = 'properties/didSave',
    propertiesDidReset = 'properties/didReset',
    propertiesDidCancel = 'properties/didCancel',
    propertiesDidAction = 'properties/didAction',
    propertiesDidCompile = 'properties/didCompile',
    editorLoadView = 'editor/loadView',
    generateCode = 'codegen/generateCode',
    metadataDidReload = 'metadata/didReload',
    metadataGetDatasets = 'metadata/getDatasets',
    generateExpressionSuggestions = 'copilot/getExpression',
    generateGraphSuggestions = 'copilot/getGraph',
    getSuggestionGraphDifference = 'copilot/getGraphDifference',
    generateModificationSuggestions = 'copilot/modifyGraph',
    copilotDescribe = 'copilot/describe',
    getLabelSuggestion = 'copilot/getLabel',
    getScriptSuggestion = 'copilot/getScript',
    getUdf = 'copilot/getUdf',
    getMacro = 'copilot/getMacro',
    getGenericDataTest = 'copilot/getGenericDataTest',
    copilotFix = 'copilot/fix',
    copilotSpecFlowChatHistory = 'copilot/specflowChatHistory',
    getCopilotGraphById = 'copilot/context',
    suggestGemProperties = 'copilot/suggestGemProperties',
    suggestPrompts = 'copilot/suggestPrompts',
    getBusinessRule = 'copilot/getBusinessRule',
    getBusinessRuleChatHistory = 'copilot/getBusinessRuleChatHistory',
    dataExplorerSaveAsGem = 'dataexplorer/saveAsGem',

    pong = 'pong',
    metadataCreateJob = 'metadata/createJob',
    backendLanguageDidChange = 'beLanguage/didChange',

    // Copilot ProjectAgent Chat API
    loadConversation = 'copilot/agent/loadConversation',
    createConversation = 'copilot/agent/createConversation',
    listConversations = 'copilot/agent/listConversations',
    deleteConversation = 'copilot/agent/deleteConversation',
    renameConversation = 'copilot/agent/renameConversation',
    copilotAgentUndo = 'copilot/agent/undo',
    copilotAgentChat = 'copilot/agent/chat',
    copilotAgentStop = 'copilot/agent/stop',
    copilotAgentUserInputResponse = 'copilot/agent/userInputResponse',

    // Upload copilot notification send
    copilotAgentFileUploaded = 'copilot/agent/filesUploaded',
    // Locks
    lockRequest = 'lock/didRequest',
    lockRelease = 'lock/didRelease',
    lockTakeOver = 'lock/didTakeover',
    lockTakeOverRespond = 'lock/didRespondTakeover',
    lockOwnerUpdate = 'lock/didUpdateOwner',
    broadcastMessage = 'message/broadcast',
    copilotKinesisChat = 'copilot/kinesis/chat',
    copilotDataExplorerChat = 'copilot/dataExplorer/chat',
    getPipelineGraphs = 'properties/getPipelineGraphs',
    getPipelineConfigurations = 'properties/getPipelineConfigurations',
    mappingsDidGetMergedTable = 'mappings/didGetMergedTable',
    mappingsUploadTable = 'mappings/uploadTable',
    mappingsCreatePipeline = 'mappings/createPipeline',
    mappingsDidRefreshAll = 'mappings/didRefreshAll',
    expressionGetUsedColumns = 'expression/getUsedColumns',
    gemGetSql = 'gem/getSql',
    resolveJinjaStrings = 'properties/resolveJinjaStrings',
    setTableParameters = 'properties/setTableParameters',
    resetTableParameters = 'properties/resetTableParameters',
    suggestColumnMapping = 'dataset/suggestColumnMapping',

    // Relayed orchestrator file sync (orchestration fabrics only, gated by the relayed-file-sync flag).
    // connectReady: UI tells editorWeb the relay path to the orchestrator is live.
    rdcConnectReady = 'rdc/connectReady',
    // relay: UI forwards the orchestrator's reply back to editorWeb (request).
    rdcRelay = 'rdc/relay',
    // run/prepare: editorWeb generates + syncs files itself for a run, keyed by runId.
    runPrepare = 'run/prepare',
    // Exact row count for one interim, computed on demand. The number comes back on a
    // `selectiveRecordsCount` notification rather than as this request's reply.
    selectiveRecordsCount = 'selectiveRecordsCount'
  }

  export type Error = {
    message: string;
    data?: string;
    code?: number;
  };

  export type Success = {
    message: string;
    type: string;
  };

  /**
   * Return type of the LSP callback function.
   *
   * **NOTE**: If the function doesn't return success as { message: string; type: string }, skip using this type.
   */
  export type CallbackReturnType = {
    data?: Success;
    error?: Error;
  };
}
